function getAnimationName(root) {
    return root?.dataset?.animate || '';
}

function getTargetSelector(root, animation) {
    return root?.dataset?.animateTarget || animation?.targetSelector || '[data-animate-item]';
}

function readNumberData(root, key) {
    const value = root?.dataset?.[key];
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function isInViewport(root, threshold = 0.18) {
    if (!root?.getBoundingClientRect) return false;
    if (typeof window === 'undefined') return false;

    const rect = root.getBoundingClientRect();
    const visibleHeight = Math.min(window.innerHeight, rect.bottom) - Math.max(0, rect.top);
    if (visibleHeight <= 0) return false;

    const height = rect.height || (rect.bottom - rect.top);
    if (height <= 0) return false;

    return visibleHeight / height >= threshold;
}

function getParallaxTarget(root, node) {
    const selector = node?.dataset?.animateParallaxTarget;
    if (selector && typeof root?.querySelector === 'function') {
        return root.querySelector(selector) || node;
    }

    return node;
}

function getParallaxStrength(node) {
    const strength = readNumberData(node, 'animateParallax');
    return Number.isFinite(strength) ? strength : 0.08;
}

function createAnimator(options = {}) {
    const registry = new Map();
    const instances = new WeakMap();
    const roots = new Set();
    const parallaxItems = new Map();
    const prefersReducedMotion =
        options.motion === 'reduced' ||
        (options.motion !== 'full' &&
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const settings = {
        once: options.once !== false,
        observe: options.observe !== false && !prefersReducedMotion,
        threshold: Number.isFinite(options.threshold) ? options.threshold : 0.18,
        rootMargin: options.rootMargin || '0px',
        motion: prefersReducedMotion ? 'reduced' : 'full',
        debug: options.debug === true,
    };
    let observer = null;
    let queuedFallbackFrame = null;
    let parallaxFrame = null;
    let parallaxListening = false;
    let parallaxHandlers = null;

    function debugLog(message, payload) {
        if (!settings.debug || typeof console === 'undefined') {
            return;
        }

        if (payload !== undefined) {
            console.info(`[speeder-animations] ${message}`, payload);
            return;
        }

        console.info(`[speeder-animations] ${message}`);
    }

    function describeRoot(root) {
        if (!root) return null;

        return {
            animation: root.dataset?.animate || '',
            state: root.dataset?.animateState || '',
            ready: root.dataset?.animateReady || '',
            motion: root.dataset?.animateMotion || '',
            targetSelector: root.dataset?.animateTarget || '',
            rect: typeof root.getBoundingClientRect === 'function'
                ? (() => {
                      const rect = root.getBoundingClientRect();
                      return {
                          top: Math.round(rect.top),
                          bottom: Math.round(rect.bottom),
                          height: Math.round(rect.height),
                      };
                  })()
                : null,
        };
    }

    function describeTarget(target) {
        if (!target) return null;

        if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
            return {
                delay: target.style?.getPropertyValue?.('--animate-delay') || '',
                opacity: '',
                transform: '',
                transitionDelay: '',
                transitionProperty: '',
            };
        }

        const computed = window.getComputedStyle(target);
        return {
            delay: target.style?.getPropertyValue?.('--animate-delay') || '',
            opacity: computed.opacity,
            transform: computed.transform,
            transitionDelay: computed.transitionDelay,
            transitionProperty: computed.transitionProperty,
        };
    }

    function logTargetSnapshot(stage, target) {
        if (!settings.debug) return;
        const snapshot = describeTarget(target);
        if (!snapshot) return;

        debugLog(`${stage} target snapshot`, {
            opacity: snapshot.opacity,
            transform: snapshot.transform,
            transitionDelay: snapshot.transitionDelay,
            transitionProperty: snapshot.transitionProperty,
            delay: snapshot.delay,
        });
    }

    function updateParallax() {
        if (!parallaxItems.size || typeof window === 'undefined') {
            return;
        }

        const viewportCenter = window.innerHeight / 2;
        parallaxItems.forEach(item => {
            const target = item?.target;
            if (!target?.getBoundingClientRect || !target?.style) {
                return;
            }

            const rect = target.getBoundingClientRect();
            const elementCenter = rect.top + rect.height / 2;
            const translate = Math.round((viewportCenter - elementCenter) * item.strength);
            target.style.transform = `translate3d(0, ${translate}px, 0)`;
            target.style.willChange = 'transform';
        });
    }

    function scheduleParallaxUpdate() {
        if (!parallaxItems.size) {
            return;
        }

        if (parallaxFrame !== null) {
            return;
        }

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            parallaxFrame = window.requestAnimationFrame(() => {
                parallaxFrame = null;
                updateParallax();
            });
            return;
        }

        updateParallax();
    }

    function ensureParallaxListeners() {
        if (parallaxListening || !parallaxItems.size || typeof window === 'undefined') {
            return;
        }

        parallaxHandlers = {
            onScroll: () => scheduleParallaxUpdate(),
            onResize: () => scheduleParallaxUpdate(),
        };

        window.addEventListener('scroll', parallaxHandlers.onScroll, { passive: true });
        window.addEventListener('resize', parallaxHandlers.onResize, { passive: true });
        parallaxListening = true;
        scheduleParallaxUpdate();
    }

    function prepareParallax(root) {
        if (settings.motion === 'reduced' || !root?.querySelectorAll) {
            return;
        }

        const nodes = Array.from(root.querySelectorAll('[data-animate-parallax]'));
        if (!nodes.length) {
            return;
        }

        let added = 0;
        nodes.forEach(node => {
            if (parallaxItems.has(node)) {
                return;
            }

            const target = getParallaxTarget(root, node);
            if (!target) {
                return;
            }

            parallaxItems.set(node, {
                target,
                strength: getParallaxStrength(node),
            });
            added += 1;
        });

        if (added && settings.debug) {
            debugLog('parallax prepared', {
                items: parallaxItems.size,
            });
        }

        ensureParallaxListeners();
    }

    function destroyParallax() {
        if (typeof window !== 'undefined' && parallaxHandlers) {
            window.removeEventListener('scroll', parallaxHandlers.onScroll);
            window.removeEventListener('resize', parallaxHandlers.onResize);
        }

        if (parallaxFrame !== null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(parallaxFrame);
        }

        parallaxItems.clear();
        parallaxFrame = null;
        parallaxHandlers = null;
        parallaxListening = false;
    }

    function register(animation) {
        if (!animation || !animation.name) {
            throw new Error('Animation definition must include a name.');
        }
        registry.set(animation.name, animation);
        return animator;
    }

    function unregister(name) {
        registry.delete(name);
        return animator;
    }

    function getDefinition(root) {
        const name = getAnimationName(root);
        return name ? registry.get(name) : null;
    }

    function prepareRoot(root) {
        const animation = getDefinition(root);
        if (!animation || root.dataset.animateInitialized === '1') {
            if (settings.debug && !animation) {
                debugLog('skip root: unknown animation', describeRoot(root));
            }
            return null;
        }

        const targetSelector = getTargetSelector(root, animation);
        const items = Array.from(root.querySelectorAll(targetSelector));
        if (!items.length) {
            debugLog(`skip ${animation?.name || 'unknown'}: no targets`, {
                targetSelector,
            });
            return null;
        }

        debugLog(`prepare ${animation.name}`, {
            root: describeRoot(root),
            targets: items.length,
            targetSelector,
        });
        logTargetSnapshot(`prepare ${animation.name}`, items[0]);

        const context =
            typeof animation.prepare === 'function'
                ? animation.prepare(root, items, {
                      targetSelector,
                      root,
                      prefersReducedMotion: settings.motion === 'reduced',
                      delay: readNumberData(root, 'animateDelay'),
                      stagger: readNumberData(root, 'animateStagger'),
                      duration: readNumberData(root, 'animateDuration'),
                  }) || {}
                : {};

        root.dataset.animateInitialized = '1';
        root.dataset.animateArmed = '0';
        instances.set(root, {
            animation,
            context,
            played: false,
            queued: false,
        });
        roots.add(root);
        return root;
    }

    function playRoot(root) {
        const instance = instances.get(root);
        if (!instance || instance.played) {
            if (instance?.played) {
                debugLog(`skip play ${instance.animation.name}: already played`, describeRoot(root));
            }
            return;
        }

        const targetCandidate =
            instance.context?.items?.[0] ||
            (typeof root?.querySelector === 'function'
                ? root.querySelector(getTargetSelector(root, instance.animation))
                : null);

        if (typeof instance.animation.play === 'function') {
            debugLog(`play ${instance.animation.name}`, {
                root: describeRoot(root),
            });
            logTargetSnapshot(`before play ${instance.animation.name}`, targetCandidate);
            instance.animation.play(root, instance.context, {
                root,
                once: settings.once,
                motion: settings.motion,
            });
            debugLog(`played ${instance.animation.name}`, {
                root: describeRoot(root),
            });
            logTargetSnapshot(`after play ${instance.animation.name}`, targetCandidate);
        } else if (root?.dataset) {
            root.dataset.animateState = 'visible';
        }

        instance.played = true;
    }

    function queuePlayRoot(root, reason = 'queued') {
        const instance = instances.get(root);
        if (!instance || instance.played || instance.queued) {
            return;
        }

        instance.queued = true;
        debugLog(`queue ${instance.animation.name}`, {
            reason,
            root: describeRoot(root),
        });

        const run = () => {
            const currentInstance = instances.get(root);
            if (!currentInstance || currentInstance.played) {
                return;
            }

            currentInstance.queued = false;
            playRoot(root);
        };

        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => {
                if (root?.dataset) {
                    root.dataset.animateArmed = '1';
                }
                window.requestAnimationFrame(run);
            });
            return;
        }

        if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
            window.setTimeout(() => {
                if (root?.dataset) {
                    root.dataset.animateArmed = '1';
                }
                window.setTimeout(run, 0);
            }, 0);
            return;
        }

        if (root?.dataset) {
            root.dataset.animateArmed = '1';
        }
        run();
    }

    function init(root = typeof document !== 'undefined' ? document : null) {
        if (!root?.querySelectorAll) return animator;

        const nodes = Array.from(root.querySelectorAll('[data-animate]'));
        debugLog('init', {
            roots: nodes.length,
            observe: settings.observe,
            motion: settings.motion,
        });
        if (!nodes.length) {
            debugLog('no [data-animate] roots found');
        }
        nodes.forEach(prepareRoot);
        prepareParallax(root);

        if (!settings.observe || typeof IntersectionObserver === 'undefined') {
            debugLog('intersection observer unavailable; playing immediately');
            roots.forEach(rootNode => queuePlayRoot(rootNode, 'no-observer'));
            return animator;
        }

        if (!observer) {
            observer = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        debugLog('observer entry', {
                            intersecting: entry.isIntersecting,
                            ratio: Number.isFinite(entry.intersectionRatio)
                                ? Math.round(entry.intersectionRatio * 100) / 100
                                : null,
                            root: describeRoot(entry.target),
                        });
                        if (!entry.isIntersecting) return;
                        queuePlayRoot(entry.target, 'observer');
                        if (settings.once) {
                            observer.unobserve(entry.target);
                        }
                    });
                },
                {
                    threshold: settings.threshold,
                    rootMargin: settings.rootMargin,
                }
            );
        }

        roots.forEach(rootNode => observer.observe(rootNode));
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            queuedFallbackFrame = window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    let queuedByViewportFallback = 0;
                    roots.forEach(rootNode => {
                        if (isInViewport(rootNode, settings.threshold)) {
                            queuedByViewportFallback += 1;
                            queuePlayRoot(rootNode, 'viewport-fallback');
                        }
                    });
                    debugLog('viewport fallback', {
                        matched: queuedByViewportFallback,
                        roots: roots.size,
                    });
                    queuedFallbackFrame = null;
                });
            });
        }
        return animator;
    }

    function refresh(root = typeof document !== 'undefined' ? document : null) {
        if (!root?.querySelectorAll) return animator;
        const nodes = Array.from(root.querySelectorAll('[data-animate]'));
        debugLog('refresh', {
            roots: nodes.length,
        });
        nodes.forEach(node => {
            if (node.dataset?.animateInitialized === '1') return;
            prepareRoot(node);
            if (observer && settings.observe) {
                observer.observe(node);
            }
        });
        prepareParallax(root);
        return animator;
    }

    function destroy() {
        if (observer) {
            roots.forEach(rootNode => observer.unobserve(rootNode));
            observer.disconnect();
        }
        if (queuedFallbackFrame !== null && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
            window.cancelAnimationFrame(queuedFallbackFrame);
        }
        destroyParallax();
        roots.clear();
        observer = null;
        queuedFallbackFrame = null;
        debugLog('destroy');
        return animator;
    }

    const animator = {
        register,
        unregister,
        init,
        refresh,
        destroy,
        get registry() {
            return registry;
        },
    };

    if (Array.isArray(options.animations)) {
        options.animations.forEach(register);
    }

    if (settings.debug && typeof window !== 'undefined') {
        window.__SPEEDER_ANIMATIONS_DEBUG_STATE__ = {
            motion: settings.motion,
            observe: settings.observe,
            once: settings.once,
        };
    }

    return animator;
}

export { createAnimator };
